import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, UserCheck, X } from 'lucide-react';
import { useHandoffNotifications, HandoffNotice } from '../../hooks/useHandoffNotifications';
import { rfqPath } from '../../utils/wizardRoutes';

/**
 * App-wide toast stack for handoff events (bottom-right, above everything).
 * Fed by useHandoffNotifications (polled while the tab is visible); each toast
 * auto-dismisses, closes on ✕, or opens its RFQ. Mounted once in App so it
 * shows wherever the user is — dashboard, list, or mid-wizard.
 */
const iconFor = (kind: HandoffNotice['kind']) =>
  kind === 'fill'
    ? { Icon: UserCheck, tile: 'border-robbie-border bg-ai-chip', ink: 'text-robbie-ink' }
    : { Icon: CheckCircle2, tile: 'border-success-border bg-success-bg', ink: 'text-success-ink' };

export const HandoffNoticeStack: React.FC = () => {
  const navigate = useNavigate();
  const { notices, dismiss } = useHandoffNotifications();

  if (notices.length === 0) return null;

  return (
    <div
      className="fixed bottom-[20px] right-[20px] z-50 flex w-[360px] max-w-[calc(100vw-40px)] flex-col gap-s3 font-sans"
      role="status"
      aria-live="polite"
    >
      {notices.map((notice) => {
        const { Icon, tile, ink } = iconFor(notice.kind);
        return (
          <div
            key={notice.key}
            className="flex items-start gap-s4 rounded-card border border-line bg-surface px-s5 py-s4 shadow-lg"
          >
            <span className={`mt-[1px] flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-control border ${tile}`}>
              <Icon className={`h-[14px] w-[14px] ${ink}`} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="m-0 text-row-title text-ink">{notice.title}</p>
              <p className="m-0 mt-[2px] text-body-sm text-ink-secondary">{notice.message}</p>
              <button
                type="button"
                onClick={() => {
                  dismiss(notice.key);
                  navigate(rfqPath(notice.rfqId));
                }}
                className="mt-s2 text-[12px] font-semibold text-action hover:underline"
              >
                Open →
              </button>
            </div>
            <button
              type="button"
              onClick={() => dismiss(notice.key)}
              aria-label="Dismiss notification"
              className="shrink-0 text-ink-muted transition-colors hover:text-ink"
            >
              <X className="h-[14px] w-[14px]" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default HandoffNoticeStack;
